/**
 * Branding utility functions for Flux AI.
 * The rebrand from "Copilot" to "Flux" is complete - these functions always return "Flux" branded text.
 */

/**
 * Returns the AI product name.
 * @returns "Flux"
 */
export const getAIProductName = (): string => "Flux";

/**
 * Returns the AI menu name.
 * @returns "Agent"
 */
export const getChatName = (): string => "Agent";

/**
 * Returns the chat feature name.
 * @returns "AI Chat"
 */
export const getChatFeatureName = (): string => "AI Chat";

/**
 * Returns the AI credits name.
 * @returns "AI credits"
 */
export const getAICreditsName = (): string => "AI credits";

/**
 * Returns the AI credits name in title case for section headings.
 * @returns "AI Credits"
 */
export const getAICreditsTitle = (): string => "AI Credits";

/**
 * Returns the full AI name.
 * @returns "Flux AI"
 */
export const getFullAIName = (): string => "Flux AI";

/**
 * Returns "Ask Flux".
 * @returns The appropriate text for asking the AI
 */
export const getAskAIText = (): string => "Ask Flux";

/**
 * Returns the shared credits name.
 * @returns "Shared AI credits"
 */
export const getSharedCreditsName = (): string => "Shared AI credits";

/**
 * Returns the shared credits name in title case for section headings.
 * @returns "Shared AI Credits"
 */
export const getSharedCreditsTitle = (): string => "Shared AI Credits";

/**
 * Returns "Chat with AI".
 * @returns The appropriate text for chatting with AI
 */
export const getChatWithAIText = (): string => "Chat with AI";

/**
 * Returns the search with AI text.
 * @returns "Search in Chat"
 */
export const getSearchWithAIText = (): string => "Search in Chat";

/**
 * Returns the tag line.
 * @returns The appropriate tag line text
 */
export const getTagLineText = (): string => "Build professional PCBs with Flux AI";

/**
 * Returns the app description.
 * @returns The appropriate app description text
 */
export const getAppDescriptionText = (): string =>
    "Go 10x faster from idea to PCB by reducing busy work, never starting from scratch, and keeping your team in sync. Now with the help of Flux AI.";

/**
 * Returns the plan description for AI features.
 * @returns "Flux AI"
 */
export const getAIPlanText = (): string => "Flux AI";

/**
 * Returns the shared AI text for plans.
 * @returns "Shared AI credits"
 */
export const getSharedAIText = (): string => "Shared AI credits";

/**
 * Composes AI credits text.
 * @param credits - The number of credits
 * @returns Formatted text for AI credits
 */
export const composeAICreditText = (credits: number): string => `Flux AI (includes ${credits} credits per month)`;

/**
 * Composes team AI credits text.
 * @param credits - The number of credits
 * @returns Formatted text for team AI credits
 */
export const composeTeamAICreditText = (credits: number): string =>
    `Flux AI (includes ${credits} credits per editor per month)`;

/**
 * Returns the tutorial name.
 * @returns "Flux Video Tutorial"
 */
export const getTutorialName = (): string => "Flux Video Tutorial";

/**
 * Returns the suggestions acceptance commit message.
 * @returns "Accepted suggestions from @ai"
 */
export const getSuggestionsCommitMessage = (): string => "Accepted suggestions from @ai";

/**
 * Returns the chat placeholder text.
 * @param hasElements - Whether the document has elements
 * @returns The appropriate placeholder text
 */
export const getChatPlaceholderText = (_hasElements: boolean): string => {
    return "Ask a question or tell Flux what to do...";
};

/**
 * Returns the chat title for empty/untitled threads.
 * @returns "Chat"
 */
export const getEmptyChatTitle = (): string => "Chat";

/**
 * Creates a branding-aware strings object that can be used in components.
 * @returns An object with branded string methods
 */
export const createBrandedStrings = () => ({
    getTagLine: () => getTagLineText(),
    getAppDescription: () => getAppDescriptionText(),
    getAIPlan: () => getAIPlanText(),
    getSharedAI: () => getSharedAIText(),
    composeAICredits: (credits: number) => composeAICreditText(credits),
    composeTeamAICredits: (credits: number) => composeTeamAICreditText(credits),
});
